import React, { useState } from 'react';
import {
  Box,
  VStack,
  Input,
  Image,
  Flex,
  useToast,
  Text,
  Button,
  Icon,
  FormControl,
  FormLabel,
  FormErrorMessage,
  InputGroup,
  InputRightElement,
  Heading,
  Link as ChakraLink
} from "@chakra-ui/react";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { signUp, confirmSignUp } from 'aws-amplify/auth';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import colors from "../../theme/foundation/colors";
import ALJLogin from "../../assets/ALJ-login.png";
import { LOGIN_ROUTE } from '../../utils/constants/routes';

const SignUpPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [verificationStep, setVerificationStep] = useState<'SIGN_UP' | 'CONFIRM'>('SIGN_UP');
  const [verificationCode, setVerificationCode] = useState('');
  
  const toast = useToast();
  const navigate = useNavigate();

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!email) newErrors.email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(email)) newErrors.email = "Email is invalid";
    
    if (!password) newErrors.password = "Password is required";
    else if (password.length < 8) newErrors.password = "Password must be at least 8 characters";
    
    if (!confirmPassword) newErrors.confirmPassword = "Please confirm your password";
    else if (password !== confirmPassword) newErrors.confirmPassword = "Passwords do not match";
    
    if (!firstName) newErrors.firstName = "First name is required";
    if (!lastName) newErrors.lastName = "Last name is required";
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSignUp = async () => {
    if (!validateForm()) return;
    
    setIsLoading(true);
    
    try {
      const lowercaseEmail = email.toLowerCase();


      const response = await fetch(`https://mbbvjpxvbjvpk3e73p3bu4uu5u0xkayj.lambda-url.eu-west-1.on.aws/?username=${lowercaseEmail}`);
      const data = await response.json();

      let userExists = false;

      if (data.exists !== undefined) {
          userExists = data.exists;
      } else if (data.body) {
          const parsedBody = JSON.parse(data.body);
          userExists = parsedBody.exists;
      }


      if (userExists) {
          toast({
            title: "User Already Exists",
            description: "An account with this email already exists. Please login instead.",
            status: "error",
            duration: 5000,
            isClosable: true,
            position: "top",
          });
          return;
      }

      await signUp({
        username: lowercaseEmail,
        password,
        options: {
          userAttributes: {
            email: lowercaseEmail,
            given_name: firstName,
            family_name: lastName
          }
        }
      });
      
      setVerificationStep('CONFIRM');
      toast({
        title: "Verification Required",
        description: "Please check your email for a verification code",
        status: "success",
        duration: 5000,
        isClosable: true,
        position: "top",
      });
    } catch (error) {
      toast({
        title: "Sign Up Failed",
        description: error instanceof Error ? error.message : "An error occurred during sign up",
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmSignUp = async () => {
    if (!verificationCode) {
      setErrors({...errors, verificationCode: "Verification code is required"});
      return;
    }
    
    setIsLoading(true);
    
    try {
      const lowercaseEmail = email.toLowerCase();
      
      await confirmSignUp({
        username: lowercaseEmail,
        confirmationCode: verificationCode
      });
      
      toast({
        title: "Success",
        description: "Your account has been verified successfully. You can now log in.",
        status: "success",
        duration: 5000,
        isClosable: true,
        position: "top",
      });
      
      navigate(LOGIN_ROUTE);
    } catch (error) {
      toast({
        title: "Verification Failed",
        description: error instanceof Error ? error.message : "An error occurred during verification",
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Flex height="100vh" width="100vw" bg={colors.backgroundMain}>
      <Box width="55%" bg={colors.teal} />
      <Box width="45%" bg="gray.100" />
      <Flex
        position="absolute"
        top="0"
        left="0"
        right="0"
        bottom="0"
        justifyContent="center"
        alignItems="center"
      >
        <Box 
          width={["90%", "500px"]}
          borderRadius="lg" 
          boxShadow="xl" 
          bg="white" 
          p={8}
        >
          <VStack spacing={6} align="stretch">
            <Flex justify="center" align="center" mb={2}>
              <Image src={ALJLogin} alt="Abdul Latif Jameel" height="50px" />
            </Flex>
            
            <Heading size="md" textAlign="center" color={colors.slate}>
              {verificationStep === 'SIGN_UP' ? 'Create a New Account' : 'Verify Your Email'}
            </Heading>
            
            {verificationStep === 'SIGN_UP' ? (
              <VStack spacing={4} align="stretch">
                <Flex gap={4}>
                  <FormControl isInvalid={!!errors.firstName}>
                    <FormLabel>First Name</FormLabel>
                    <Input 
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      placeholder="First Name"
                      size="lg"
                    />
                    <FormErrorMessage>{errors.firstName}</FormErrorMessage>
                  </FormControl>

                  <FormControl isInvalid={!!errors.lastName}>
                    <FormLabel>Last Name</FormLabel>
                    <Input 
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      placeholder="Last Name"
                      size="lg"
                    />
                    <FormErrorMessage>{errors.lastName}</FormErrorMessage>
                  </FormControl>
                </Flex>

                <FormControl isInvalid={!!errors.email}>
                  <FormLabel>Email</FormLabel>
                  <Input 
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Email"
                    size="lg"
                  />
                  <FormErrorMessage>{errors.email}</FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={!!errors.password}>
                  <FormLabel>Password</FormLabel>
                  <InputGroup size="lg">
                    <Input 
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Password (minimum 8 characters)"
                      size="lg"
                    />
                    <InputRightElement width="4.5rem" height="100%">
                      <Button
                        h="1.75rem"
                        size="sm"
                        onClick={() => setShowPassword(!showPassword)}
                        variant="ghost"
                      >
                        <Icon as={showPassword ? FaEyeSlash : FaEye} />
                      </Button>
                    </InputRightElement>
                  </InputGroup>
                  <FormErrorMessage>{errors.password}</FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={!!errors.confirmPassword}>
                  <FormLabel>Confirm Password</FormLabel>
                  <InputGroup size="lg">
                    <Input 
                      type={showConfirmPassword ? "text" : "password"}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Confirm Password"
                      size="lg"
                    />
                    <InputRightElement width="4.5rem" height="100%">
                      <Button
                        h="1.75rem"
                        size="sm"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        variant="ghost"
                      >
                        <Icon as={showConfirmPassword ? FaEyeSlash : FaEye} />
                      </Button>
                    </InputRightElement>
                  </InputGroup>
                  <FormErrorMessage>{errors.confirmPassword}</FormErrorMessage>
                </FormControl>

                <Button
                  onClick={handleSignUp}
                  isLoading={isLoading}
                  size="lg"
                  height="56px"
                  width="100%"
                  backgroundColor="#006778"
                  color="white"
                  _hover={{
                    backgroundColor: "#005666",
                    transform: "translateY(-2px)",
                    boxShadow: "md"
                  }}
                  _active={{
                    backgroundColor: "#004555",
                    transform: "translateY(0)",
                  }}
                  mt={4}
                >
                  SIGN UP
                </Button>
              </VStack>
            ) : (
              <VStack spacing={4} align="stretch">
                <Text>
                  We've sent a verification code to <strong>{email}</strong>. 
                  Please enter the code below to verify your account.
                </Text>

                <FormControl isInvalid={!!errors.verificationCode}>
                  <FormLabel>Verification Code</FormLabel>
                  <Input 
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value)}
                    placeholder="Enter the code from your email"
                    size="lg"
                  />
                  <FormErrorMessage>{errors.verificationCode}</FormErrorMessage>
                </FormControl>

                <Button
                  onClick={handleConfirmSignUp}
                  isLoading={isLoading}
                  size="lg"
                  height="56px"
                  width="100%"
                  backgroundColor="#006778"
                  color="white"
                  _hover={{
                    backgroundColor: "#005666",
                    transform: "translateY(-2px)",
                    boxShadow: "md"
                  }}
                  _active={{
                    backgroundColor: "#004555",
                    transform: "translateY(0)",
                  }}
                  mt={4}
                >
                  VERIFY ACCOUNT
                </Button>
              </VStack>
            )}

            <Flex justify="center" mt={2}>
              <Text fontSize="sm" color="gray.600">
                Already have an account?{" "}
                <ChakraLink
                  as={RouterLink}
                  to={LOGIN_ROUTE}
                  color="teal.600"
                  _hover={{ color: "teal.700", textDecoration: "underline" }}
                >
                  Sign in
                </ChakraLink>
              </Text>
            </Flex>
          </VStack>
        </Box>
      </Flex>
    </Flex>
  );
};

export default SignUpPage; 